import { useEffect, useState, useMemo } from 'react'
import AdminLayout from '../../layouts/AdminLayout'
import {
    Box, Button, Flex, HStack,
    Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    FormControl, FormLabel, Grid, Input, Select, IconButton,
} from "@chakra-ui/react";
import { FaEdit, FaFileCsv, FaFilePdf } from "react-icons/fa";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import adminService from '../../services/adminService';

import ReactPaginate from "react-paginate";
import { exportToPDF } from '../../utils/pdfExporter';
import { exportToCSV } from '../../utils/csvExporter';

const validationSchema = Yup.object().shape({
    OverlapThreshold: Yup.string().required("Overlap Threshold is required"),
    OverlapStep: Yup.string().required("Overlap Step is required"),
    overlapPercentage: Yup.string().required("Overlap Percentage is required"),
    saveLatLngSeconds: Yup.string().required("LatLong in Seconds is Required"),
    CompCd: Yup.string().required("Company is Required"),
    defaultRate: Yup.string().required("Default Rate is Required"),
    Stat: Yup.string().required("Stat is Required"),
    compMobile: Yup.string().required("Company Mobile is Required"),
    aajaaMobile: Yup.string().required("Aajaa Mobile is Required"),

});

const ITEMS_PER_PAGE = 10;

const Config = () => {

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        reset
    } = useForm({
        resolver: yupResolver(validationSchema),
    });

    const [data, setData] = useState([])
    const [CNo, setCNo] = useState(0);
    const [company, setCompany] = useState([]);

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState("asc");

    useEffect(() => {
        configList();
        companyList();
    }, [])

    const configList = async () => {
        try {
            const res = await adminService.getConfigs()

            if (res.statusCode === 200) {
                setData(res.data)
            }
        } catch (error) {
            alert(error.response?.data?.message || "Something went wrong.");
        }
    }

    const companyList = async () => {
        setCompany([])
        try {
            const res = await adminService.getCompany();
            if (res.statusCode === 200) {
                setCompany(res.data)
            }
        } catch (error) {
            alert(error.response?.data?.message || "Something went wrong.");
        }
    }

    const handleConfig = async (data) => {
        console.log(`dd`, data)
        data.CNo = CNo;
        try {
            const res = await adminService.saveConfig(data)

            setCNo(0)
            if (res.statusCode === 200) {
                alert(res.message);
                reset();
                configList();
            }
        } catch (error) {
            alert(error.response?.data?.message || "Something went wrong.");
        }
    }

    const handleEdit = (row) => {
        setValue("OverlapThreshold", row.OverlapThreshold);
        setValue("OverlapStep", row.OverlapStep);
        setValue("overlapPercentage", row.overlapPercentage);
        setValue("saveLatLngSeconds", row.saveLatLngSeconds);
        setValue("CompCd", row.CompCd);
        setValue("defaultRate", row.defaultRate);
        setValue("aajaaMobile", row.aajaaMobile);
        setValue("compMobile", row.compMobile);
        setValue("Stat", row.Stat);
        setCNo(row.CNo)
    };

    const filteredData = useMemo(() => {
        let result = [...data];
        if (search) {
            const lowerSearch = search.toLowerCase();
            result = result.filter((item) =>
            (`${item.OverlapThreshold}`.toLowerCase().includes(lowerSearch) ||
                item.CompName.toLowerCase().includes(lowerSearch) ||
                item.aajaaMobile.toLowerCase().includes(lowerSearch) ||
                item.compMobile.toLowerCase().includes(lowerSearch) ||
                String(item.OverlapStep).includes(lowerSearch))
            );
        }
        if (sortField) {
            result.sort((a, b) => {
                let aVal = a[sortField] ?? '';
                let bVal = b[sortField] ?? '';

                return sortOrder === "asc" ? aVal > bVal ? 1 : -1 : aVal < bVal ? 1 : -1;
            });
        }
        return result;
    }, [data, search, sortField, sortOrder]);

    const pageCount = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

    const currentData = useMemo(() => {
        const start = page * ITEMS_PER_PAGE;
        return filteredData.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredData, page]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
    };

    const handlePageChange = ({ selected }) => {
        setPage(selected);
    };

    const exportCSV = () => {
        // data, filename
        exportToCSV(filteredData, "config.csv")
    };

    const exportPDF = () => {
        const columns = [
            { label: "CNo", accessor: "CNo" },
            { label: "Company", accessor: "CompName" },
            { label: "Threshold", accessor: "OverlapThreshold" },
            { label: "Step", accessor: "OverlapStep" },
            { label: "Percentage", accessor: "overlapPercentage" },
            { label: "Seconds", accessor: "saveLatLngSeconds" },
            { label: "DefaultRate", accessor: "defaultRate" },
            { label: "aajaaMobile", accessor: "aajaaMobile" },
            { label: "compMobile", accessor: "compMobile" },
            { label: "Status", accessor: "Stat" },
        ];

        exportToPDF({
            data: filteredData,      // your actual data array
            columns,
            fileName: "config.pdf"
        });
    };

    return (
        <AdminLayout>
            <Box maxW="1000px" mx="auto" px={4}>
                {/* Form Section */}
                <Box
                    bg="white"
                    p={6}
                    rounded="xl"
                    boxShadow="md"
                    mb={3}
                    border="1px solid"
                    borderColor="gray.200"
                >
                    <form onSubmit={handleSubmit(handleConfig)}>
                        <Grid
                            templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                            gap={6}
                        >
                            <FormControl >
                                <FormLabel fontSize="sm">Company</FormLabel>
                                <Select size="sm" {...register("CompCd")} >
                                    <option value=''>Select</option>
                                    {company.map((row, i) => (
                                        <option value={row.CompCd} key={row.CompCd}>{row.Name}</option>
                                    ))}
                                </Select>
                                <Box color="red.500" fontSize="xs">
                                    {errors.CompCd?.message}
                                </Box>
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="sm">Threshold</FormLabel>
                                <Input type="number" {...register("OverlapThreshold")} size="sm" />
                                <Box color="red.500" fontSize="xs">{errors.OverlapThreshold?.message}</Box>
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="sm">Overlap Step</FormLabel>
                                <Input type="number" {...register("OverlapStep")} size="sm" />
                                <Box color="red.500" fontSize="xs">{errors.OverlapStep?.message}</Box>
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="sm">Overlap Percentage</FormLabel>
                                <Input type="number" {...register("overlapPercentage")} size="sm" />
                                <Box color="red.500" fontSize="xs">{errors.overlapPercentage?.message}</Box>
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="sm">LatLngSeconds</FormLabel>
                                <Input type="number" {...register("saveLatLngSeconds")} size="sm" />
                                <Box color="red.500" fontSize="xs">{errors.saveLatLngSeconds?.message}</Box>
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="sm">Default Rate</FormLabel>
                                <Input type="text" {...register("defaultRate")} size="sm" />
                                <Box color="red.500" fontSize="xs">{errors.defaultRate?.message}</Box>
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="sm">aajaaMobile</FormLabel>
                                <Input type="text" {...register("aajaaMobile")} size="sm" />
                                <Box color="red.500" fontSize="xs">{errors.aajaaMobile?.message}</Box>
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="sm">compMobile</FormLabel>
                                <Input type="text" {...register("compMobile")} size="sm" />
                                <Box color="red.500" fontSize="xs">{errors.compMobile?.message}</Box>
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="sm">Status</FormLabel>
                                <Select {...register("Stat")} size="sm">
                                    <option value={0}>Enable</option>
                                    <option value={1}>Disable</option>
                                </Select>
                                <Box color="red.500" fontSize="xs">{errors.Stat?.message}</Box>
                            </FormControl>

                        </Grid>

                        <Button type="submit" colorScheme="blue" mt={6} size="sm">
                            Submit
                        </Button>
                    </form>
                </Box>

                {/* Table + Controls Section */}
                <Box
                    bg="white"
                    p={6}
                    rounded="xl"
                    boxShadow="md"
                    border="1px solid"
                    borderColor="gray.200"
                >
                    <Flex justify="space-between" mb={4} flexWrap="wrap" gap={4}>
                        <Input
                            placeholder="Search..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(0);
                            }}
                            maxW="300px"
                            size="sm"
                        />
                        <HStack>
                            <Button
                                size="sm"
                                colorScheme="blue"
                                leftIcon={<FaFileCsv />}
                                onClick={exportCSV}
                            >
                                CSV
                            </Button>
                            <Button
                                size="sm"
                                colorScheme="red"
                                leftIcon={<FaFilePdf />}
                                onClick={exportPDF}
                            >
                                PDF
                            </Button>
                        </HStack>
                    </Flex>
                    <TableContainer overflowX="auto">
                        <Table size="sm" variant="simple">
                            <Thead bg="gray.100">
                                <Tr>
                                    <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("CompName")}>Company</Th>
                                    <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("OverlapThreshold")}>Threshold</Th>
                                    <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("OverlapStep")}>Step</Th>
                                    <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("overlapPercentage")}>Percentage</Th>
                                    <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("saveLatLngSeconds")}>Lat Long</Th>
                                    <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("defaultRate")}>Default Rate</Th>
                                    <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("defaultRate")}>AAjaa Mobile</Th>
                                    <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("defaultRate")}>Comp Mobile</Th>
                                    <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("Stat")}>Status</Th>
                                    <Th px={2} py={1} >Action</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {currentData.map((row, index) => (
                                    <Tr key={row.CNo}>
                                        <Td>{row.CompName}</Td>
                                        <Td>{row.OverlapThreshold}</Td>
                                        <Td>{row.OverlapStep}</Td>
                                        <Td>{row.overlapPercentage}</Td>
                                        <Td>{row.saveLatLngSeconds}</Td>
                                        <Td>{row.defaultRate}</Td>
                                        <Td>{row.aajaaMobile}</Td>
                                        <Td>{row.compMobile}</Td>
                                        <Td>{row.Stat === 0 ? "Enable" : "Disable"}</Td>
                                        <Td>
                                            <IconButton
                                                icon={<FaEdit />}
                                                aria-label="Edit"
                                                borderRadius="full"
                                                onClick={() => handleEdit(row)}
                                            />
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                </Box>

                <Box mt={6}>
                    <ReactPaginate
                        previousLabel="Previous"
                        nextLabel="Next"
                        pageCount={pageCount}
                        onPageChange={handlePageChange}
                        containerClassName="pagination"
                        activeClassName="active"
                        pageClassName="page-item"
                        previousClassName="page-item"
                        nextClassName="page-item"
                        breakClassName="page-item"
                        pageLinkClassName="page-link"
                        previousLinkClassName="page-link"
                        nextLinkClassName="page-link"
                        breakLinkClassName="page-link"
                    />
                </Box>

                <style>{`
                                              .pagination {
                                                display: flex;
                                                justify-content: center;
                                                list-style: none;
                                                padding: 0;
                                                margin-top: 1rem;
                                              }
                                              .page-item {
                                                margin: 0 5px;
                                              }
                                              .page-link {
                                                padding: 8px 12px;
                                                border-radius: 6px;
                                                background-color: #e2e8f0;
                                                cursor: pointer;
                                              }
                                              .page-link:hover {
                                                background-color: #cbd5e0;
                                              }
                                              .active .page-link {
                                                background-color: #3182ce;
                                                color: white;
                                              }
                                            `}</style>

            </Box>
        </AdminLayout>
    )
}

export default Config
